import type { Tender } from '~/models/tenders';
import type { TenderCopyPreviewOffer } from 'endpoints.tenders';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button as ShadcnButton } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from 'Components/nui';
import Alert from 'Components/nui/Alert';
import DatePicker from 'Components/nui/DatePicker';
import Icon from 'Components/Icon';
import { OrderDetails } from '../OrderDetails';
import * as service from '~/services/tenders';
import { routeUrl } from '~/router';
import {
  TenderCopyPreviewError,
  useTenderCopyPreview,
} from '~/hooks/useTenderCopyPreview';

type Props = { tender: Tender };

const NEEDS_REVIEW_MESSAGES = {
  both: (
    <FormattedMessage
      id="tender-copy-offer-needs-review-both"
      defaultMessage="Previous attributes and loading configurations are unavailable. Review before publishing."
    />
  ),
  attributes: (
    <FormattedMessage
      id="tender-copy-offer-needs-review-attributes"
      defaultMessage="The previous attributes are unavailable and will be removed. Review before publishing."
    />
  ),
  loading: (
    <FormattedMessage
      id="tender-copy-offer-needs-review-loading"
      defaultMessage="The previous loading configuration is no longer active. Review before publishing."
    />
  ),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const needsReviewMessage = (order: any) => {
  if (order.attributes_need_review && order.loading_needs_review)
    return NEEDS_REVIEW_MESSAGES.both;
  if (order.attributes_need_review) return NEEDS_REVIEW_MESSAGES.attributes;
  if (order.loading_needs_review) return NEEDS_REVIEW_MESSAGES.loading;
  return null;
};

// Adapt a TenderCopyPreviewOffer into the order-like shape OrderDetails
// (and the StackItem JSX it renders) expects. When the offer is index-priced
// we forward the full `index` object from the preview so Data.Price /
// Data.IndexUnit render the same way as the tender review screen.
const toOrder = (offer: TenderCopyPreviewOffer, index: number) =>
  ({
    id: `${offer.product.id}-${index}`,
    product: offer.product,
    state: { fixed_index: offer.fixed_index },
    startprice: {
      // Default to 4 decimals when the backend doesn't return step/precision.
      // For index offers `index.step` is read instead (see below).
      precision: 4,
      ...offer.startprice,
      ...(offer.indexdate ? { indexdate: offer.indexdate } : {}),
      // `step` arrives from the BE as a string ("0.0025"); Data.Number /
      // getDecimalCount expect a number.
      ...(offer.index
        ? { index: { ...offer.index, step: parseFloat(offer.index.step) } }
        : {}),
    },
    volume: offer.volume,
    etd: {
      from: offer.frometd ?? offer.toetd ?? '',
      to: offer.toetd ?? offer.frometd ?? '',
    },
    attributes_need_review: offer.attributes_need_review,
    loading_needs_review: offer.loading_needs_review,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any;

export const CopyTender = ({ tender }: Props) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [start, setStart] = useState<string | undefined>();
  const [startError, setStartError] = useState<string | undefined>();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const { data, error, isLoading, isError, isFetching } = useTenderCopyPreview(
    tender.id,
    {
      start,
      enabled: open,
    }
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setStart(undefined);
      setStartError(undefined);
    }
    setOpen(next);
  };

  useEffect(() => {
    if (
      open &&
      error instanceof TenderCopyPreviewError &&
      error.status === 403
    ) {
      toast.error(
        formatMessage({
          id: 'tender-copy-error-permission',
          defaultMessage: 'You do not have permission to copy this tender.',
        })
      );
      handleOpenChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, error]);

  const previewOffers = data?.tender.offers ?? [];
  const sourceOfferTotal = tender.orders?.total ?? 0;
  const someExcluded =
    !!data && sourceOfferTotal > 0 && previewOffers.length < sourceOfferTotal;

  const defaultStart = data?.tender.start;
  const selectedStart = start ?? defaultStart;

  const onSubmit = async () => {
    setSubmitting(true);
    setStartError(undefined);
    const result = await service.actions.copyTender(tender.id, {
      start: selectedStart,
      cadence: data?.tender.cadence,
    });
    setSubmitting(false);

    if (result?.success) {
      toast.success(
        formatMessage({
          id: 'tender-copy-success',
          defaultMessage: 'Tender copied. You can now edit the new tender.',
        })
      );
      handleOpenChange(false);
      history.push(
        routeUrl('tender-edit-details', {
          tenderId: result.data.id,
          mode: 'edit',
        }),
        { expandOffers: true }
      );
      return;
    }

    if (result?.validate) {
      setStartError(String(result.validate));
      return;
    }

    if (result?.errors?.includes('Permission denied')) {
      toast.error(
        formatMessage({
          id: 'tender-copy-error-permission',
          defaultMessage: 'You do not have permission to copy this tender.',
        })
      );
      handleOpenChange(false);
      return;
    }

    toast.error(
      formatMessage({
        id: 'tender-copy-error',
        defaultMessage: 'Could not copy tender. Please try again.',
      })
    );
  };

  return (
    <>
      <Button onClick={() => handleOpenChange(true)}>
        <FormattedMessage
          id="tender-button-actions-copy"
          description="Button for `Copy tender` in tender owner actions dropdown"
          defaultMessage="Copy tender"
        />
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="tw tw:sm:max-w-xl tw:max-h-[90vh] tw:flex tw:flex-col">
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="tender-copy-heading"
                defaultMessage="Copy tender"
              />
            </DialogTitle>
            <DialogDescription asChild>
              <div className="tw tw:flex tw:flex-col tw:gap-2 tw:mb-5">
                <FormattedMessage
                  id="tender-copy-body"
                  defaultMessage="<p>A copy of this tender will be created using your previous tender details.</p><p>Update the pre-filled start date to see how other dates shift automatically while maintaining their relative timing.</p><p>You can make further changes before publishing, and no notifications will be sent until the tender is published.</p>"
                  values={{
                    p: (chunks: React.ReactNode) => (
                      <p className="tw:p-0 tw:m-0">{chunks}</p>
                    ),
                  }}
                />
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="tw tw:flex-1 tw:overflow-y-auto tw:flex tw:flex-col tw:gap-4 tw:min-h-0 tw:-mx-1 tw:px-1 tw:py-3 tw:border-y tw:border-border">
            {isError ? (
              <Alert type="error" hasicon>
                <FormattedMessage
                  id="tender-copy-preview-error"
                  defaultMessage="Could not load preview. Please try again."
                />
              </Alert>
            ) : (
              <>
                {someExcluded && (
                  <Alert type="warning" hasicon>
                    <FormattedMessage
                      id="tender-copy-warning-filtered"
                      defaultMessage="Inactive participants and products have been excluded."
                    />
                  </Alert>
                )}

                <div className="tw">
                  <label className="tw:text-sm tw:font-medium tw:mb-2">
                    <FormattedMessage
                      id="tender-copy-start-date-label"
                      defaultMessage="Start"
                    />
                  </label>
                  {defaultStart && (
                    <DatePicker
                      selectsRange={false}
                      size=""
                      futureTimeOnly={false}
                      showTimeSelect
                      timeIntervals={5}
                      dateFormat="d MMM yyyy h:mm a"
                      initialValue={selectedStart}
                      onChange={(d: Date | null) => {
                        if (d) {
                          setStart(dayjs(d).toISOString());
                          setStartError(undefined);
                        }
                      }}
                    />
                  )}
                  {startError && (
                    <p className="tw:text-destructive tw:text-sm tw:m-0">
                      {startError}
                    </p>
                  )}
                </div>

                <div
                  className={`tw tw:flex tw:flex-col tw:gap-3 tw:transition-opacity ${
                    isFetching && !isLoading
                      ? 'tw:opacity-50 tw:pointer-events-none'
                      : ''
                  }`}
                >
                  <h3 className="tw:font-semibold tw:m-0 tw:text-primary! tw:text-sm">
                    <FormattedMessage
                      id="tender-copy-offers-heading"
                      defaultMessage="Included offers"
                    />
                  </h3>
                  {isLoading && !data ? (
                    <div className="tw:text-sm tw:text-muted-foreground">
                      <FormattedMessage
                        id="tender-copy-loading"
                        defaultMessage="Loading…"
                      />
                    </div>
                  ) : (
                    <div className="tenders2">
                      <div className="wizard">
                        <OrderDetails
                          orders={previewOffers.map(toOrder)}
                          titleSuffix={(order: TenderCopyPreviewOffer) =>
                            (order.attributes_need_review ||
                              order.loading_needs_review) && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="tw:inline-flex tw:items-center tw:cursor-help tw:bg-transparent tw:border-0 tw:p-0 tw:ml-1 tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-ring"
                                    data-testid="copy-tender-offer-warning"
                                    aria-label={formatMessage({
                                      id: 'tender-copy-offer-needs-review-aria',
                                      defaultMessage: 'Needs review',
                                    })}
                                  >
                                    <Icon
                                      name="warning"
                                      size="small"
                                      className="tw:text-red-600"
                                      style={{
                                        fontVariationSettings: "'FILL' 1",
                                      }}
                                    />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {needsReviewMessage(order)}
                                </TooltipContent>
                              </Tooltip>
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <ShadcnButton
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              <FormattedMessage
                id="tender-copy-button-cancel"
                defaultMessage="Cancel"
              />
            </ShadcnButton>
            <ShadcnButton
              onClick={() => void onSubmit()}
              disabled={submitting || isLoading || isError}
            >
              <FormattedMessage
                id="tender-copy-button-submit"
                defaultMessage="Copy tender"
              />
            </ShadcnButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CopyTender;
